import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { CheckIcon, CopyIcon } from "lucide-react";

type CopyableFieldProps = {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  copiedValue: string | null;
  onCopy: (value: string) => void;
  href?: string;
  size?: "sm" | "md";
  /** Extra classes of the copy button (e.g. a larger touch target). */
  buttonClassName?: string;
};

export default function CopyableField({
  icon: Icon,
  label,
  value,
  copiedValue,
  onCopy,
  href,
  size = "md",
  buttonClassName,
}: CopyableFieldProps) {
  const isCopied = copiedValue === value;
  const isSmall = size === "sm";

  const iconSize = isSmall ? "w-4 h-4" : "w-4 h-4";
  const iconWrapperSize = isSmall ? "w-6 h-6" : "w-8 h-8";
  const labelSize = isSmall ? "text-[11px] leading-none" : "text-xs";
  const valueSize = isSmall ? "text-sm font-medium" : "text-sm font-semibold";
  const btnSize = isSmall ? "w-6 h-6" : "w-7 h-7";
  const checkSize = isSmall ? "w-3 h-3" : "w-3.5 h-3.5";

  const valueElement = href ? (
    <a href={href} className={`${valueSize} truncate hover:underline`}>
      {value}
    </a>
  ) : (
    <span className={`${valueSize} truncate`}>{value}</span>
  );

  return (
    <div className="flex items-center gap-2">
      <div className={`flex items-center justify-center ${iconWrapperSize} rounded-lg bg-muted shrink-0`}>
        <Icon className={`${iconSize} text-muted-foreground`} />
      </div>
      <div className="flex flex-col min-w-0 flex-1">
        <span className={`${labelSize} text-muted-foreground`}>
          {label}
        </span>
        {valueElement}
      </div>
      <Button
        variant="ghost"
        size="icon"
        className={cn(btnSize, "shrink-0", buttonClassName)}
        onClick={() => onCopy(value)}
      >
        {isCopied ? (
          <CheckIcon className={`${checkSize} text-emerald-500`} />
        ) : (
          <CopyIcon className={`${checkSize} text-muted-foreground`} />
        )}
      </Button>
    </div>
  );
}
